import type { RouteRecordRaw } from 'vue-router';
import { UserRole, PermissionAction, System, PermissionsAlias } from './roles';
import { RolePermissionConfig, type PermissionKey } from './permissions';
import { AgentRolePermissionConfig } from './permissions-agent';

/**
 * 从菜单路由中查找第一个可访问的路由路径，即用户的「首页」
 * @param routes 过滤后的路由列表（来自 permission store）
 * @returns 第一个可访问的菜单路由路径，如果没有则返回 null
 */
export function findFirstMenuRoute(routes: RouteRecordRaw[]): string | null {
  for (const route of routes) {
    // 跳过没有路径的路由
    if (!route.path) continue;

    // 如果有子路由，递归查找子路由
    if (route.children && route.children.length > 0) {
      const childPath = findFirstMenuRoute(route.children);
      if (childPath) return childPath;
    }

    // 如果是菜单项（showInMenu 为 true 且不是 hidden）且有名称
    if (route.name && route.meta?.showInMenu === true && !route.meta?.hidden) {
      if (!route.children) {
        return route.path.startsWith('/') ? route.path : `/${route.path}`;
      }
    }
  }

  return null;
}

/**
 * 检查用户是否有权限访问该路由
 * @param userRole 用户角色ID
 * @param route 路由配置
 * @param userSystem 用户所属系统 ('business' | 'agent')
 */
export function hasRoutePermission(
  userRole: UserRole | null,
  route: RouteRecordRaw,
  userSystem: string | null = null
): boolean {
  const routeName = route.name as string;
  if (!routeName) return true; // 没有名称的路由（如布局路由）默认可访问

  // 如果路由的 meta 中设置了 requiresAuth: false，则不需要权限验证，所有人都可以访问
  if (route.meta?.requiresAuth === false) {
    return true;
  }

  // 如果没有角色，默认无权限
  if (!userRole) return false;

  // 根据用户系统选择权限配置
  let permissionConfig: Record<UserRole, PermissionKey[] | PermissionsAlias>

  if (userSystem === System.business) {
    permissionConfig = RolePermissionConfig
  } else if (userSystem === System.agent) {
    permissionConfig = AgentRolePermissionConfig
  } else {
    // 如果没有指定系统，尝试从 Business 配置获取（向后兼容）
    console.warn(`未指定用户系统，使用 Business 系统权限配置`);
    permissionConfig = RolePermissionConfig
  }

  // 获取该角色的权限配置
  const permissions = permissionConfig[userRole];

  // 如果角色不在配置中，记录警告并返回无权限
  if (!permissions) {
    console.warn(`角色 ${userRole} 在系统 ${userSystem} 中不在权限配置中，拒绝访问路由: ${routeName}`);
    return false;
  }

  // 如果权限配置为 'has_all_permission'，则认为有所有权限
  if (permissions === PermissionsAlias.hasAllPermission) {
    return true;
  }

  // 检查是否有 show 权限（有 show 权限才能访问路由）
  const showPermission = `${routeName}:${PermissionAction.SHOW}`;
  return permissions.includes(showPermission as any);
}

/**
 * 在路由列表中根据路由名称查找路由路径
 * @param routes 路由配置列表
 * @param routeName 路由名称
 * @returns 路由路径，如果没有则返回 null
 */
function findRoutePathByName(routes: RouteRecordRaw[], routeName: string): string | null {
  for (const route of routes) {
    if (route.name === routeName) {
      return route.path.startsWith('/') ? route.path : `/${route.path}`;
    }

    if (route.children && route.children.length > 0) {
      const childPath = findRoutePathByName(route.children, routeName);
      if (childPath) return childPath;
    }
  }

  return null;
}

/**
 * 根据路由名称查找路由对象
 */
function findRouteByName(routes: RouteRecordRaw[], routeName: string): RouteRecordRaw | null {
  for (const route of routes) {
    if (route.name === routeName) return route;

    if (route.children && route.children.length > 0) {
      const found = findRouteByName(route.children, routeName);
      if (found) return found;
    }
  }

  return null;
}
